// What the task's header says about it.
//
// It showed three grey chips of equal weight — MEDIUM, sms, and the raw enum
// "inprogress" — none in a colour that meant anything. The header is where
// every pair of eyes lands first, so it has three jobs: say where the task is
// in plain words with a colour that means it; shout only when something is
// actually urgent; and put the quiet facts on one line rather than a row of
// badges that all look equally important.

export type ChipColor = 'default' | 'warning' | 'success' | 'error';

const STATUS: Record<string, { label: string; color: ChipColor }> = {
  todo: { label: 'To do', color: 'default' },
  inprogress: { label: 'In progress', color: 'warning' },
  completed: { label: 'Completed', color: 'success' },
};

// "snoozed_until" -> "Snoozed until": never a raw enum on screen, even one
// this file has not met.
const humanise = (value: string): string => {
  const words = value.replace(/[_-]+/g, ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1).toLowerCase() : '';
};

export function statusChip(status: string | null | undefined): { label: string; color: ChipColor } {
  const key = String(status || 'todo').toLowerCase();
  return STATUS[key] || { label: humanise(key), color: 'default' };
}

// A badge only when it needs to be: medium and low are not news.
export function priorityChip(priority: string | null | undefined): { label: string; color: ChipColor } | null {
  switch (String(priority || '').toLowerCase()) {
    case 'urgent': return { label: 'Urgent', color: 'error' };
    case 'high': return { label: 'High priority', color: 'warning' };
    default: return null;
  }
}

const CHANNELS: Record<string, string> = {
  sms: 'SMS', email: 'Email', fax: 'Fax', voicemail: 'Voicemail', phone: 'Phone', web: 'Web',
};

export function headerSubtitle({
  category, modality, priority,
}: { category?: string | null; modality?: string | null; priority?: string | null }): string {
  const parts: string[] = [];
  if (category) parts.push(humanise(String(category)));
  if (modality) parts.push(CHANNELS[String(modality).toLowerCase()] || humanise(String(modality)));
  // The priorities worth a badge are on the badge; the others read here.
  if (priority && !priorityChip(priority)) parts.push(`${humanise(String(priority))} priority`);
  return parts.join(' · ');
}
